export interface EdrItem {
  // BE level string(s) for this level, matched case-insensitively. Holds both
  // old (4-level) and new (5-level) values during the rollout.
  beValues: readonly string[];
  iconClass: string;
  colorClass: string;
  label: string;
  description: string;
}

// Order matches the Figma tooltip, which iterates this array directly. Not
// severity order; nothing derives a ranking from it.
export const EDR_ITEMS: readonly EdrItem[] = [
  {
    beValues: ["Active Exploit Prevention", "Pre-exploitation prevention"],
    iconClass: "seem-icon-sheild-checkmark",
    colorClass: "edr-icon--active-prevention",
    label: "Active exploit prevention",
    description:
      "Specific mitigations or precise rule matches are actively blocking exploitation of this finding",
  },
  {
    beValues: ["Well-Configured EDR", "Advanced EDR protection"],
    iconClass: "seem-icon-sheild-checkmark",
    colorClass: "edr-icon--well-configured",
    label: "Well-configured EDR",
    description:
      "Full recommended prevention and remediation posture is active on the resource",
  },
  {
    beValues: ["Detection only EDR", "Pre-exploitation detection"],
    iconClass: "seem-icon-alert-shield",
    colorClass: "edr-icon--detection-only",
    label: "Detection only EDR",
    description:
      "Security controls are in monitor/audit mode; threats are logged but not blocked",
  },
  {
    // BE sends "Partial Configured EDR" ("Partial", not "Partially").
    beValues: ["Partial Configured EDR"],
    iconClass: "seem-icon-alert-shield",
    colorClass: "edr-icon--partially-configured",
    label: "Partially configured EDR",
    description: "Some prevention rules are active, but coverage is incomplete",
  },
  {
    beValues: ["Ineffective EDR", "Advanced EDR detection"],
    iconClass: "seem-icon-shield",
    colorClass: "edr-icon--ineffective",
    label: "Ineffective EDR",
    description:
      "Minimal or no meaningful EDR controls are active on the resource",
  },
];

// Sentinel `level` for the aggregated-coverage no-data bucket. Not a real BE
// `mitigation_level` — chosen to never collide with one so it can route through
// the same row component as real-level buckets.
export const EDR_NO_DATA_LEVEL = "__no_data__";

export const EDR_NO_DATA_LABEL = "Don't have an EDR";

export type EdrLevelMeta =
  | {
      variant: "data";
      iconClass: string;
      colorClass: string;
      // Undefined for an unmatched level; callers fall back to the raw value.
      displayLabel?: string;
    }
  | {
      variant: "no-data";
      barClass: string;
      displayLabel: string;
    };

export const getEdrLevelMeta = (level: string): EdrLevelMeta => {
  if (level === EDR_NO_DATA_LEVEL) {
    return {
      variant: "no-data",
      barClass: "edr-mitigation-coverage-bar--no-data",
      displayLabel: EDR_NO_DATA_LABEL,
    };
  }
  const normalizedLevel = level.toLowerCase();
  const item = EDR_ITEMS.find(({ beValues }) =>
    beValues.some((beValue) => beValue.toLowerCase() === normalizedLevel)
  );
  if (item) {
    return {
      variant: "data",
      iconClass: item.iconClass,
      colorClass: item.colorClass,
      displayLabel: item.label,
    };
  }
  return {
    variant: "data",
    iconClass: "seem-icon-warning",
    colorClass: "edr-icon--no-data",
  };
};

// Resolves a BE level string to the display label shown across the table,
// details header, remediation section and coverage rows, falling back to the
// raw value when the level is unmatched.
export const resolveEdrDisplayLabel = (level: string): string =>
  getEdrLevelMeta(level).displayLabel ?? level;
